import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Trophy, Users, Gift, Coins, Zap, ShieldCheck,
  RefreshCcw, Lock, Unlock, Search, Edit3, CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const EMPTY_CAMPAIGN = {
  title: "Community Vault",
  description: "Work together to unlock this month's exclusive community reward.",
  artwork_url: "",
  reward_title: "Mystery Community Reward",
  reward_description: "A special surprise awaits inside the Vault.",
  community_goal: 500,
  claim_hours_after_unlock: 168,
  is_active: true,
  is_visible: true,
  allow_coin_contributions: true,
  coin_min: 10,
  coin_max: 500,
  coin_to_progress: 0.1,
  daily_coin_limit: 200,
  campaign_coin_limit: 2000,
  purchase_progress: 1,
};

export default function AdminVault() {
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [editing, setEditing] = useState(null); // campaign object or null
  const [showForm, setShowForm] = useState(false);
  const [manual, setManual] = useState({ member_id: "", value: 1, note: "" });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [{ data: c }, { data: a }, { data: k }] = await Promise.all([
        api.get("/admin/vault/campaigns"),
        api.get("/admin/vault/analytics"),
        api.get("/admin/vault/contributions"),
      ]);
      setCampaigns(c || []);
      setAnalytics(a);
      setContributions(k || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing({ ...EMPTY_CAMPAIGN }); setShowForm(true); };
  const startEdit = (c) => { setEditing({ ...c }); setShowForm(true); };
  const cancelEdit = () => { setEditing(null); setShowForm(false); };

  const saveCampaign = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = {
        title: editing.title,
        description: editing.description,
        artwork_url: editing.artwork_url || null,
        reward_title: editing.reward_title,
        reward_description: editing.reward_description,
        community_goal: parseInt(editing.community_goal) || 100,
        start_date: editing.start_date || null,
        end_date: editing.end_date || null,
        claim_hours_after_unlock: parseInt(editing.claim_hours_after_unlock) || 168,
        is_active: !!editing.is_active,
        is_visible: !!editing.is_visible,
        allow_coin_contributions: !!editing.allow_coin_contributions,
        coin_min: parseInt(editing.coin_min) || 1,
        coin_max: parseInt(editing.coin_max) || 500,
        coin_to_progress: parseFloat(editing.coin_to_progress) || 0.1,
        daily_coin_limit: parseInt(editing.daily_coin_limit) || 200,
        campaign_coin_limit: parseInt(editing.campaign_coin_limit) || 2000,
        purchase_progress: parseInt(editing.purchase_progress) || 1,
      };
      if (editing.id) {
        await api.put(`/admin/vault/campaigns/${editing.id}`, payload);
        toast.success("Campaign updated");
      } else {
        await api.post("/admin/vault/campaigns", payload);
        toast.success("Campaign created");
      }
      cancelEdit();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeCampaign = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all its contributions? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/vault/campaigns/${c.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitManual = async () => {
    if (!manual.member_id) return toast.error("Enter a member ID or code");
    setBusy(true);
    try {
      await api.post("/admin/vault/contribute-manual", {
        member_id: manual.member_id,
        value: parseInt(manual.value) || 1,
        note: manual.note || "",
      });
      toast.success("Contribution added");
      setManual({ member_id: "", value: 1, note: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const filteredContribs = contributions.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (c.member_id || "").toLowerCase().includes(s)
      || (c.member_code || "").toLowerCase().includes(s)
      || (c.source || "").toLowerCase().includes(s)
      || (c.note || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-vault-page">
      <header className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Community</div>
          <h1 className="editorial-h1 text-4xl">Community Vault</h1>
        </div>
        <button onClick={startCreate} className="btn-pill btn-dark text-xs" data-testid="vault-new-campaign">
          <Plus size={12} /> New Campaign
        </button>
      </header>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <Stat icon={Trophy} label="Active campaigns" value={analytics.active_campaigns} accent />
          <Stat icon={Zap} label="Total contributions" value={analytics.total_contributions} />
          <Stat icon={Users} label="Unique contributors" value={analytics.unique_contributors} />
          <Stat icon={Gift} label="Vault openings" value={analytics.total_openings} />
          <Stat icon={Coins} label="Coin contribs value" value={(analytics.by_source || []).find((b) => b.source === "coins")?.value || 0} />
        </div>
      )}

      {/* Campaigns list */}
      <div className="rounded-2xl bg-white border border-brand-line p-5 mb-6">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-4">All campaigns</div>
        {campaigns.length === 0 ? (
          <div className="text-center text-brand-charcoal/50 py-8">No campaigns yet. Create your first one!</div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => {
              const p = c.community_goal ? Math.min(100, Math.round((c.current_progress / c.community_goal) * 100)) : 0;
              return (
                <div key={c.id} className="rounded-xl border border-brand-line p-4" data-testid={`vault-campaign-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display text-xl">{c.title}</div>
                        <StatusPill status={c.status} active={c.is_active} visible={c.is_visible} />
                      </div>
                      <div className="text-sm text-brand-charcoal/70 mt-1">{c.description}</div>
                      <div className="mt-3 flex items-center gap-3 max-w-md">
                        <div className="flex-1 h-2 rounded-full bg-brand-muted overflow-hidden">
                          <div className="h-full bg-brand-yellow" style={{ width: `${p}%` }} />
                        </div>
                        <div className="text-xs font-mono">{c.current_progress}/{c.community_goal} ({p}%)</div>
                      </div>
                      <div className="mt-2 text-xs text-brand-charcoal/60">
                        Reward: <span className="text-brand-charcoal">{c.reward_title}</span> · Contributors: {c.contributing_members_count || 0}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => startEdit(c)} className="btn-pill btn-ghost text-xs" data-testid={`vault-edit-${c.id}`}><Edit3 size={11} /> Edit</button>
                      <button onClick={() => removeCampaign(c)} className="btn-pill text-xs text-red-600 hover:bg-red-50" data-testid={`vault-delete-${c.id}`}><Trash2 size={11} /> Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Manual contribute */}
      <div className="rounded-2xl bg-white border border-brand-line p-5 mb-6">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-3">Manually add contribution (active campaign)</div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Member ID or code</label>
            <input value={manual.member_id} onChange={(e) => setManual({ ...manual, member_id: e.target.value })} className="pp-input" placeholder="PPXYZ123 or full id" data-testid="vault-manual-member" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Points to add</label>
            <input type="number" min="1" value={manual.value} onChange={(e) => setManual({ ...manual, value: e.target.value })} className="pp-input" data-testid="vault-manual-value" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Note</label>
            <input value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} className="pp-input" placeholder="e.g. offline event" data-testid="vault-manual-note" />
          </div>
          <button onClick={submitManual} disabled={busy} className="btn-pill btn-dark text-xs disabled:opacity-60" data-testid="vault-manual-submit">
            <Plus size={12} /> Add contribution
          </button>
        </div>
      </div>

      {/* Contributions table */}
      <div className="rounded-2xl bg-white border border-brand-line p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Contributions log</div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/45" />
              <input value={q} onChange={(e) => setQ(e.target.value)} className="pp-input pl-8" placeholder="Search member / source / note" data-testid="vault-contrib-search" />
            </div>
            <button onClick={load} className="btn-pill btn-ghost text-xs" data-testid="vault-refresh"><RefreshCcw size={11} /> Refresh</button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">
            <tr>
              <th className="text-left py-2">Member</th>
              <th className="text-left py-2">Source</th>
              <th className="text-left py-2">Points</th>
              <th className="text-left py-2">Coins</th>
              <th className="text-left py-2">Note</th>
              <th className="text-left py-2">At</th>
            </tr>
          </thead>
          <tbody>
            {filteredContribs.length === 0 ? (
              <tr><td colSpan={6} className="py-8 text-center text-brand-charcoal/45">No contributions yet.</td></tr>
            ) : filteredContribs.slice(0, 200).map((c) => (
              <tr key={c.id} className="border-t border-brand-line" data-testid={`vault-contrib-${c.id}`}>
                <td className="py-2 font-mono text-xs">{c.member_code || c.member_id?.slice(0, 8) + "…"}</td>
                <td className="py-2 capitalize">{c.source}</td>
                <td className="py-2 font-mono">+{c.value}</td>
                <td className="py-2 font-mono">{c.coins_used || 0}</td>
                <td className="py-2 text-xs text-brand-charcoal/70">{c.note || "—"}</td>
                <td className="py-2 text-xs text-brand-charcoal/60">{new Date(c.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal edit */}
      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={cancelEdit}>
          <div className="max-w-2xl w-full bg-white rounded-2xl p-6 overflow-y-auto max-h-[92vh]" onClick={(e) => e.stopPropagation()} data-testid="vault-form-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl">{editing.id ? "Edit Campaign" : "New Campaign"}</div>
              <button onClick={cancelEdit} className="text-brand-charcoal/60 hover:text-brand-charcoal">✕</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="pp-input" data-testid="vault-form-title" /></Field>
              <Field label="Community goal (points)"><input type="number" min="1" value={editing.community_goal} onChange={(e) => setEditing({ ...editing, community_goal: e.target.value })} className="pp-input" data-testid="vault-form-goal" /></Field>
              <Field label="Description" full><textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="pp-input" data-testid="vault-form-desc" /></Field>
              <Field label="Reward title"><input value={editing.reward_title} onChange={(e) => setEditing({ ...editing, reward_title: e.target.value })} className="pp-input" data-testid="vault-form-reward-title" /></Field>
              <Field label="Reward description"><input value={editing.reward_description} onChange={(e) => setEditing({ ...editing, reward_description: e.target.value })} className="pp-input" data-testid="vault-form-reward-desc" /></Field>
              <Field label="Artwork URL (optional)" full><input value={editing.artwork_url || ""} onChange={(e) => setEditing({ ...editing, artwork_url: e.target.value })} className="pp-input" data-testid="vault-form-artwork" /></Field>

              <Field label="Coin min"><input type="number" value={editing.coin_min} onChange={(e) => setEditing({ ...editing, coin_min: e.target.value })} className="pp-input" data-testid="vault-form-coin-min" /></Field>
              <Field label="Coin max (per contribution)"><input type="number" value={editing.coin_max} onChange={(e) => setEditing({ ...editing, coin_max: e.target.value })} className="pp-input" data-testid="vault-form-coin-max" /></Field>
              <Field label="Coin → progress ratio"><input type="number" step="0.01" value={editing.coin_to_progress} onChange={(e) => setEditing({ ...editing, coin_to_progress: e.target.value })} className="pp-input" data-testid="vault-form-ratio" /></Field>
              <Field label="Daily coin limit"><input type="number" value={editing.daily_coin_limit} onChange={(e) => setEditing({ ...editing, daily_coin_limit: e.target.value })} className="pp-input" data-testid="vault-form-daily" /></Field>
              <Field label="Campaign coin cap"><input type="number" value={editing.campaign_coin_limit} onChange={(e) => setEditing({ ...editing, campaign_coin_limit: e.target.value })} className="pp-input" data-testid="vault-form-cap" /></Field>
              <Field label="Purchase → progress"><input type="number" value={editing.purchase_progress} onChange={(e) => setEditing({ ...editing, purchase_progress: e.target.value })} className="pp-input" data-testid="vault-form-purchase" /></Field>
              <Field label="Claim window (hours)"><input type="number" value={editing.claim_hours_after_unlock} onChange={(e) => setEditing({ ...editing, claim_hours_after_unlock: e.target.value })} className="pp-input" data-testid="vault-form-claim-hours" /></Field>
            </div>

            <div className="mt-4 flex flex-wrap gap-5">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="vault-form-active" />
                Active
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.is_visible} onChange={(e) => setEditing({ ...editing, is_visible: e.target.checked })} data-testid="vault-form-visible" />
                Visible on homepage
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.allow_coin_contributions} onChange={(e) => setEditing({ ...editing, allow_coin_contributions: e.target.checked })} data-testid="vault-form-coins-on" />
                Allow Pop Coin contributions
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={cancelEdit} className="btn-pill btn-ghost text-xs" data-testid="vault-form-cancel">Cancel</button>
              <button onClick={saveCampaign} disabled={busy} className="btn-pill btn-dark text-xs disabled:opacity-60" data-testid="vault-form-save">
                <Save size={12} /> {busy ? "Saving…" : "Save Campaign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-brand-charcoal text-brand-cream border-brand-charcoal" : "bg-white border-brand-line"}`}>
      <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${accent ? "text-brand-yellow" : "text-brand-charcoal/55"}`}><Icon size={11} /> {label}</div>
      <div className="text-2xl font-display mt-1">{value ?? 0}</div>
    </div>
  );
}

function StatusPill({ status, active, visible }) {
  if (status === "unlocked") return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><Unlock size={9} /> Unlocked</span>;
  if (status === "closed") return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 inline-flex items-center gap-1"><Lock size={9} /> Closed</span>;
  if (active && visible) return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-brand-yellow/25 text-brand-charcoal inline-flex items-center gap-1"><CheckCircle2 size={9} /> Live</span>;
  if (active) return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Hidden</span>;
  return <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Draft</span>;
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">{label}</label>
      {children}
    </div>
  );
}
